import React from "react";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME } from "../Variables.js";
import { useUserAuth } from "../context/UserAuthContext";

const ProductsByCategoryPDFButton = ({ products = [] }) => {
  const { user, userData } = useUserAuth();
  const hotelName = HOTEL_NAME;
  const userDepartment = userData?.department?.trim() || null;

  const formatDateTime = (date) =>
    date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(",", "");

  const formatAmount = (amount) =>
    `N${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const checkPageBreak = (doc, finalY, requiredHeight = 0, bottomMargin = 30) => {
    const pageHeight = doc.internal.pageSize.height;
    if (finalY + requiredHeight > pageHeight - bottomMargin) {
      doc.addPage();
      return 20;
    }
    return finalY;
  };

  const generateReportPDF = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString("en-GB").replace(/\//g, "_");

    // Header
    doc.addImage(Logo, "PNG", 14, 10, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hotelName, 35, 15);
    doc.setFont("normal");
    doc.text("Products by Category", 35, 23);

    doc.setFontSize(10);
    const fullName = userData?.firstName && userData?.lastName 
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    doc.text(`Generated by: ${fullName}`, 35, 28);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);

    if (userDepartment) {
      doc.text(`Department: ${userDepartment}`, 35, 38);
    }

    let finalY = userDepartment ? 45 : 40;

    // Organize: department → category → products
    const deptGroups = {};

    products.forEach(product => {
      const dept = (product.department || "").trim();
      const category = (product.category || "Uncategorized").trim();

      if (userDepartment && dept !== userDepartment) return;

      if (!deptGroups[dept]) deptGroups[dept] = {};
      if (!deptGroups[dept][category]) deptGroups[dept][category] = [];
      deptGroups[dept][category].push(product);
    });

    // Departments: empty first, then alphabetical
    let departments = Object.keys(deptGroups);
    if (departments.includes("")) {
      departments = departments.filter(d => d !== "");
      departments.unshift("");
    }
    departments.sort((a, b) => a.localeCompare(b));

    departments.forEach((deptKey, deptIdx) => {
      const categories = deptGroups[deptKey];
      const categoryKeys = Object.keys(categories).sort();

      // Department heading
      finalY = checkPageBreak(doc, finalY, 15);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      const deptTitle = deptKey === "" ? "" : deptKey;
      doc.text(deptTitle, 14, finalY);
      finalY += 9;

      categoryKeys.forEach((catKey, catIdx) => {
        const prods = categories[catKey];

        // Category heading
        finalY = checkPageBreak(doc, finalY, 12);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(catKey, 18, finalY);
        finalY += 7;

        // Table
        const tableData = prods.map((p, i) => {
          const isActive = p.isActive ?? true;
          const qty = p.quantity || 0;
          const cost = p.costPrice || p.price || 0;
          const sell = p.price || 0;
          const value = qty * sell;
          const cst = qty * cost;
          const profit = value - cst;

          return [
            (i + 1).toString(),
            (p.name || "Unknown") + (!isActive ? " (INACTIVE)" : ""),
            qty.toString(),
            formatAmount(cost),
            formatAmount(sell),
            formatAmount(value),
            formatAmount(profit),
            isActive ? "Active" : "Inactive"
          ];
        });

        autoTable(doc, {
          startY: finalY,
          head: [["S/N", "Product Name", "Quantity", "Cost Price", "Selling Price", "Total Value", "Profit", "Status"]],
          body: tableData,
          theme: "striped",
          headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          margin: { left: 12, right: 14, bottom: 30 },
          styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 45 },
            2: { cellWidth: 18 },
            3: { cellWidth: 25 },
            4: { cellWidth: 25 },
            5: { cellWidth: 28 },
            6: { cellWidth: 25 },
            7: { cellWidth: 20 }
          },
          didDrawPage: (data) => {
            finalY = data.cursor.y + 6;
          },
          willDrawCell: (data) => {
            if (data.section === "body") {
              doc.setTextColor(0, 0, 0);
            }
            if (data.column.index === 6 && data.section === "body") {
              const val = parseFloat(data.cell.raw?.replace(/[^\d.-]/g, "") || "0");
              if (val < 0) doc.setTextColor(220, 53, 69);
              else if (val > 0) doc.setTextColor(40, 167, 69);
              else doc.setTextColor(0, 0, 0);
            }
            if (data.column.index === 1 && data.cell.raw?.includes("(INACTIVE)")) {
              doc.setTextColor(220, 53, 69);
            }
          }
        });

        // Category summary (like original per-category totals)
        const count = prods.length;
        const totalItems = prods.reduce((sum, p) => sum + (p.quantity || 0), 0);
        const totalCostPrice = prods.reduce((sum, p) => sum + (p.quantity || 0) * (p.costPrice || p.price || 0), 0);
        const totalSellingPrice = prods.reduce((sum, p) => sum + (p.quantity || 0) * (p.price || 0), 0);
        const totalProfit = totalSellingPrice - totalCostPrice;

        finalY = checkPageBreak(doc, finalY, 35);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Total Products: ${count}`, 18, finalY);
        doc.text(`Total Items: ${totalItems.toLocaleString()}`, 18, finalY + 5);
        doc.text(`Total Cost Price: ${formatAmount(totalCostPrice)}`, 18, finalY + 10);
        doc.text(`Total Selling Price: ${formatAmount(totalSellingPrice)}`, 18, finalY + 15);

        if (totalProfit < 0) doc.setTextColor(220, 53, 69);
        else if (totalProfit > 0) doc.setTextColor(40, 167, 69);
        doc.setFont("helvetica", "bold");
        doc.text(`Total Profit: ${formatAmount(totalProfit)}`, 18, finalY + 22);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        finalY += 32;

        // Small separator between categories
        if (catIdx < categoryKeys.length - 1) {
          finalY = checkPageBreak(doc, finalY, 10);
          doc.setLineWidth(0.3);
          doc.setDrawColor(160, 160, 160);
          doc.line(18, finalY, doc.internal.pageSize.width - 18, finalY);
          finalY += 12;
        }
      });

      // Separator between departments
      if (deptIdx < departments.length - 1) {
        finalY = checkPageBreak(doc, finalY, 15);
        doc.setLineWidth(0.6);
        doc.setDrawColor(100, 100, 100);
        doc.line(14, finalY, doc.internal.pageSize.width - 14, finalY);
        finalY += 18;
      }
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount} • ${hotelName}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 8);
    }

    const filename = `products_by_dept_category_${currentDate}${userDepartment ? `_${userDepartment}` : ""}.pdf`;
    doc.save(filename);
  };

  return (
    <Link
      to=""
      className="row align-center space-between first-cover monthly-report-button"
      onClick={(e) => {
        e.preventDefault();
        generateReportPDF();
      }}
      style={{ marginTop: "12px", cursor: "pointer" }}
    >
      <span className="italics size2 font-gold bold">4.</span>
      <p className="second">
        Download All Products
      </p>
    </Link>
  );
};

export default ProductsByCategoryPDFButton;